import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GridCellParams,
  GridColDef,
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowModes,
  Toolbar,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { DataGridPro, GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { Box, Button } from '@mui/material';
import { isEqual } from 'lodash';
import { getTargetAttributes, updateTargetAttributes } from '@/generated/client/targetattributes/targetattributes';
import {
  MinimalTarget,
  MinimalTargetAttributeDtoValue,
  TargetAttributeDto,
  TargetAttributeDtoType,
} from '@/generated/model';
import TypeBasedEditor from './TypeBasedEditor';
import TypeBasedDisplay from './TypeBasedDisplay';
import { SearchComponent } from '../common/Search';
import GridExport from '../common/GridExport';
import './TargetAttributes.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useNotificationStore } from '@/stores/notificationstore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DisplayNoResult from '../common/DisplayNoResult';
import { fulldate } from '@/utilities/dateUtils';

const targetAttributeTags = new Set(['vehicle', 'company', 'other']);

export default function TargetAttributesEditor({
  selectedTargets,
  onEditingChange,
}: {
  selectedTargets: MinimalTarget[] | undefined;
  onEditingChange?: (isEditing: boolean) => void;
}) {
  const { t } = useTranslation();
  const [originalRows, setOriginalRows] = useState<TargetAttributeDto[]>([]);
  const [rows, setRows] = useState<TargetAttributeDto[]>([]);
  const [changedRowKeys, setChangedRowKeys] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState<string | undefined>();
  const [updatedRowKeys, setUpdatedRowKeys] = useState<Set<string>>(new Set());
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [rowModesModel, setRowModesModel] = useState({});
  const apiRef = useGridApiRef();

  useEffect(() => {
    const isEditing = Object.values(rowModesModel).some((m: any) => m.mode === GridRowModes.Edit);
    if (isEditing) {
      onEditingChange?.(isEditing);
    }
  }, [rowModesModel]);

  {
    /*nyelváltás miatt megnyitott ablak mellett, race condition feloldására */
  }
  async function wait() {
    return new Promise((resolve) => {
      setTimeout(resolve, 200);
    });
  }

  async function loadTargetAttributes() {
    setChangedRowKeys(new Set());
    setUpdatedRowKeys(new Set());
    if (!selectedTargets || selectedTargets.length === 0) {
      setOriginalRows([]);
      setRows([]);
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    await wait();
    const perTargetAttributes = (await getTargetAttributes(selectedTargetIds)).data;
    const newTargetAttributes = perTargetAttributes.attributes;
    newTargetAttributes.sort((a, b) => a.priority - b.priority);
    setOriginalRows(newTargetAttributes);
    setRows(newTargetAttributes);
    onEditingChange?.(false);
  }

  useEffect(() => {
    void loadTargetAttributes();
  }, [selectedTargets]);

  useEffect(() => {
    if (!searchText) {
      setRows(originalRows);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = originalRows.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(lower))
    );
    setRows(filtered);
  }, [searchText, originalRows]);

  function processRowUpdate(newRow: TargetAttributeDto) {
    setRows((prev) => prev.map((prevRow) => (prevRow.key === newRow.key ? newRow : prevRow)));

    const original = originalRows.find((row) => row.key === newRow.key);
    const changed = !isEqual(newRow.value, original?.value);

    setChangedRowKeys((prev) => {
      const updated = new Set(prev);
      if (changed) {
        updated.add(newRow.key);
        onEditingChange?.(true);
      } else {
        updated.delete(newRow.key);
        onEditingChange?.(false);
      }
      return updated;
    });
    return newRow;
  }

  async function modifyAttributes() {
    if (!selectedTargets?.length || !changedRowKeys.size) {
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    const changedRows = rows.filter((row) => changedRowKeys.has(row.key));
    const changedValues = changedRows.map(({ key, type, value }) => {
      switch (type) {
        case TargetAttributeDtoType['@list']: {
          const enumValue = value?.id;
          return { key: key, value: enumValue as MinimalTargetAttributeDtoValue | undefined };
        }
        case TargetAttributeDtoType['@class']: {
          const id = value?.id;
          return { key: key, value: id as MinimalTargetAttributeDtoValue | undefined };
        }
        default:
          return { key, value };
      }
    });
    await updateTargetAttributes({ targetIds: selectedTargetIds, values: changedValues });
    showNotification('success', t('targetAttributes.successfulModification'), undefined, 3);
    setUpdatedRowKeys(changedRowKeys);
    setChangedRowKeys(new Set());
    onEditingChange?.(false);
  }

  const columns: GridColDef[] = [
    {
      // do not use value field. With that valueGetter overrides the original value
      field: 'valueName',
      headerName: t('targetAttributes.valueHeader'),
      minWidth: 150,
      maxWidth: 350,
      align: 'right',
      flex: 1,
      editable: true,
      renderEditCell: (params) => <TypeBasedEditor params={params} />,
      renderCell: (params) => <TypeBasedDisplay params={params} />,
      // Needed for show values in TypeBasedDisplay component
      valueGetter: (_, row: TargetAttributeDto) => {
        if (
          !row.value // row.value === undefined || row.value === null
        ) {
          return null;
        }
        const type: TargetAttributeDtoType = row.type;
        switch (type) {
          case TargetAttributeDtoType['@list']: {
            return row.value.translation;
          }
          case TargetAttributeDtoType['@class']: {
            return row.value.brand + ' ' + row.value.type;
          }
          default:
            return row.value;
        }
      },
      // Needed for show values in csv export
      valueFormatter: (_, row) => {
        if (!row.value) {
          return null;
        }
        const type: TargetAttributeDtoType = row.type;
        switch (type) {
          case TargetAttributeDtoType['@list']: {
            return row.value.translation;
          }
          case TargetAttributeDtoType['@class']: {
            return row.value.brand + ' ' + row.value.type;
          }
          case TargetAttributeDtoType['@date']: {
            return fulldate(row.value * 1000, t);
          }
          default:
            return row.value;
        }
      },
      valueSetter: (value, row) => {
        if (
          (row.type === TargetAttributeDtoType['@class'] || row.type === TargetAttributeDtoType['@list']) &&
          typeof value === 'string'
        ) {
          return row;
        }
        return { ...row, value: value };
      },
    },
  ];

  function getExportFilename() {
    if (selectedTargets && selectedTargets.length === 1) {
      return selectedTargets[0].name;
    } else {
      return t('targetAttributes.exportFileName');
    }
  }

  function CustomToolbar() {
    return (
      <Toolbar className={'targetattribute-toolbar'}>
        <Box sx={{ width: '60%', mr: 2 }}>
          <SearchComponent
            searchText={searchText}
            setSearchText={setSearchText}
            showHistory={false}
            placeholder={t('targetAttributes.searchPlaceHolder')}
          />
        </Box>
        <GridExport filename={getExportFilename()} isListView={false} />
      </Toolbar>
    );
  }

  function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
    const { id, field, row, rowNode, formattedValue } = props;
    const apiRef = useGridApiContext<GridApiPro>();
    const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
    const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (rowNode.type !== 'group') {
        return;
      }
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    };

    return (
      <Box
        sx={{
          ml: rowNode.depth * 4,
          width: '100%',
          minWidth: 0,
        }}
      >
        <div onClick={handleClick}>
          {rowNode.type === 'group' ? (
            <div className="attribute-category">
              {rowNode.childrenExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              <div className="attribute-category-text">
                {formattedValue} ({filteredDescendantCount})
              </div>
            </div>
          ) : (
            <div className="attribute-category-text">{row.translatedKey}</div>
          )}
        </div>
      </Box>
    );
  }

  const groupingColDef = {
    headerName: t('targetAttributes.keyHeader'),
    maxWidth: 400,
    flex: 1,
    renderCell: (params: GridRenderCellParams) => <CustomGridTreeDataGroupingCell {...params} />,
    // FIXME ne legyen any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueFormatter: (value: any, row: TargetAttributeDto) => {
      if (row.key) {
        return value;
      }
      return translateTag(value);
    },
    colSpan: (_: any, row: TargetAttributeDto) => {
      if (row.type === undefined) {
        return 2;
      }
      return 1;
    },
  };

  function translateTag(tag: string) {
    if (tag.includes('empty_tag')) {
      return '';
    } else if (targetAttributeTags.has(tag)) {
      return t('targetAttributes.type.' + tag);
    }
    return tag;
  }

  function getRowClass(params: GridRowClassNameParams) {
    const rowKey = params.row.key;
    if (rowKey === undefined) {
      const type = params.id.toString().split('auto-generated-row-null/')[1];
      if (targetAttributeTags.has(type)) {
        return 'type-row type-row-' + type;
      }
      return 'type-row type-row-itrack-param';
    }
    if (changedRowKeys.has(rowKey)) {
      return 'changed-row';
    }
    if (updatedRowKeys.has(rowKey)) {
      return 'updated-row';
    }
    return '';
  }

  function handleCellClickRowEdit(params: GridCellParams, rowModesModel: any, setRowModesModel: (model: any) => void) {
    if (params.field === '__tree_data_group__') {
      return;
    }
    const isEditing = Object.values(rowModesModel).some((m: any) => m.mode === GridRowModes.Edit);
    if (isEditing) {
      return;
    }
    setRowModesModel({
      ...rowModesModel,
      [params.id]: { mode: GridRowModes.Edit },
    });
  }

  return (
    <>
      <div className="grid-wrapper">
        <div className="target-attributes-grid">
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            disableDensitySelector
            disableColumnFilter
            disableVirtualization
            pageSizeOptions={[100]}
            getRowId={(attribute) => attribute.key}
            treeData
            getTreeDataPath={(row) => [...row.tag, row.translatedKey]}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={-1}
            processRowUpdate={processRowUpdate}
            getRowClassName={getRowClass}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => <DisplayNoResult searchText={searchText} />,
            }}
            showToolbar
            hideFooterRowCount
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onCellClick={function (params) {
              handleCellClickRowEdit(params, rowModesModel, setRowModesModel);
            }}
            sx={{
              '--DataGrid-rowBorderColor': 'transparent',
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
            }}
          />
        </div>
        {changedRowKeys.size && (
          <div className="button-container">
            <Button className="targetattribute-button save-button" onClick={modifyAttributes}>
              <CheckCircleOutlineIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.save')}</span>
            </Button>
            <Button className="targetattribute-button cancel-button" onClick={loadTargetAttributes}>
              <CancelOutlinedIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.cancel')}</span>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
